// BuildingStabilityInfoComponent.js
import { useEffect, useState } from "react";
import {
  SectionHeader,
  EditableSelectComponent,
  EditableTextareaComponent,
  EditableInputComponent,
} from "components";
import { Document } from "assets/icons"; // Replace with appropriate icons
import { useSession } from "@clerk/clerk-react";
// Assuming BuildingStabilityInfoDTO is defined elsewhere in your application
import { BuildingDTO } from "shared/types/building";
import { UnitDTO } from "shared/types/unit";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { API_URL } from "constants/index";
import { toast } from "react-toastify";
import {
  BathroomHealthStatus,
  BedroomsHealthStatus,
  CeilingStatus,
  DoorStatus,
  FloorStatus,
  MoistStatus,
  ToiletHealthStatus,
  UnitInspectionInfoDTO,
  WallStatus,
  WindowStatus,
  WindowTerraceSafetyStatus,
} from "shared/types/unit-inspection";
import { ImagesContainer } from "shared/components/imagesContainer/imagesContainer";
import { FileLabel, PictureLabel } from "shared/types/file";

interface UnitVisualInspectionInfoComponentProps {
  inspectionInfo: UnitInspectionInfoDTO;
  handleInspectionInfoChangeInParent?: (value: any) => any;
  handleSaveInParent?: () => any;
  handleCloseParent?: () => any;
  building: BuildingDTO;
  unit: UnitDTO;
  parentEditMode?: boolean;
}

const UnitVisualInspectionInfoComponent = ({
  inspectionInfo,
  handleCloseParent,
  handleInspectionInfoChangeInParent,
  building,
  unit,
  parentEditMode = true,
}: UnitVisualInspectionInfoComponentProps) => {
  const [editMode, setEditMode] = useState(parentEditMode);
  const [editedInspectionInfo, setEditedInspectionInfo] =
    useState(inspectionInfo);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const { session } = useSession();

  useEffect(() => {
    setEditedInspectionInfo(inspectionInfo);
  }, []); // don't use the dependency here because it will refresh the component on tab switch

  const handleSave = async () => {
    setHasUnsavedChanges(false);
    try {
      await fetchWithAuth(
        `${API_URL}/units/unit-inspection-info`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(editedInspectionInfo),
        },
        session,
      );
      toast.success("Visuele inspectie informatie succesvol bijgewerkt!");
    } catch (e) {
      console.error(e);
      toast.error(
        "Er is een fout opgetreden bij het bijwerken van de visuele inspectie.",
      );
    }
  };

  const updateInspectionInfo = (updatedFields: any) => {
    setHasUnsavedChanges(true);
    setEditedInspectionInfo((prev: any) => ({ ...prev, ...updatedFields }));

    if (handleInspectionInfoChangeInParent) {
      handleInspectionInfoChangeInParent({
        ...inspectionInfo,
        ...updatedFields,
      });
    }
  };

  const getStatusColor = (status: any) => {
    switch (status) {
      case WindowStatus.OK:
      case DoorStatus.OK:
      case MoistStatus.OK:
      case WindowTerraceSafetyStatus.OK:
      case WindowTerraceSafetyStatus.NOT_REQUIRED:
      case WallStatus.OK:
      case FloorStatus.OK:
      case CeilingStatus.OK:
      case BathroomHealthStatus.OK:
      case BedroomsHealthStatus.OK:
      case ToiletHealthStatus.OK:
        return "bg-green-500";
      case DoorStatus.PARTIAL_OK:
      case WindowStatus.PARTIAL_OK:
      case BathroomHealthStatus.PARTIAL_OK_LIGHT:
      case BathroomHealthStatus.PARTIAL_OK_VENTILATION:
      case BedroomsHealthStatus.PARTIAL_OK_LIGHT:
      case BedroomsHealthStatus.PARTIAL_OK_VENTILATION:
      case FloorStatus.PARTIALY_OK:
        return "bg-orange-500";
      case FloorStatus.NOT_OK:
      case CeilingStatus.NOT_OK:
      case WallStatus.NOT_OK:
      case WindowStatus.NOT_OK:
      case DoorStatus.NOT_OK:
      case MoistStatus.NOT_OK:
      case WindowTerraceSafetyStatus.NOT_OK:
      case BedroomsHealthStatus.NOT_OK:
      case ToiletHealthStatus.NOT_OK:
      case BathroomHealthStatus.NOT_OK:
        return "bg-red-500";
      default:
        return "bg-transparent";
    }
  };

  const getStatusCodesForRooms = (numRooms: any) => {
    if (!numRooms) {
      return "bg-transparent-500";
    }

    if (Number(numRooms)) {
      return "bg-green-500";
    }
  };

  const getStatusColorForCeilingHeight = (ceilingHeight: any) => {
    if (!ceilingHeight) {
      return "bg-transparent-500";
    }

    if (Number(ceilingHeight) >= 250) {
      return "bg-green-500";
    } else if (Number(ceilingHeight) >= 220 && Number(ceilingHeight) < 250) {
      return "bg-orange-500";
    } else if (Number(ceilingHeight) < 220) {
      return "bg-red-500";
    } else {
      return "bg-transparent-500";
    }
  };

  const onUpdateWindowStatusDescription = (remarks: string) => {
    updateInspectionInfo({ windowStatusDescription: remarks });
  };

  const onUpdateGeneralRemarls = (remarks: string) => {
    updateInspectionInfo({ generalRemarks: remarks });
  };

  const onUpdateDoorStatusDescription = (remarks: string) => {
    updateInspectionInfo({ doorStatusDescription: remarks });
  };

  const onUpdateMoistStatusDescription = (remarks: string) => {
    updateInspectionInfo({ moistStatusDescription: remarks });
  };

  const onUpdateWindowTerraceSafetyStatusDescription = (remarks: string) => {
    updateInspectionInfo({ windowTerraceSafetyStatusDescription: remarks });
  };

  return (
    <section className="flex flex-col gap-0 rounded-md bg-white">
      <SectionHeader
        title="Visuele inspectie Informatie"
        icon={<Document />}
        editMode={editMode}
        onEditClick={() => setEditMode(!editMode)}
        onSaveClick={() => handleSave()}
        showEditToggle={true}
        sticky={false}
        hasUnsavedChanges={hasUnsavedChanges}
        onCloseClick={handleCloseParent}
        hideCloseAction={true}
      />
      <div className="p-3">
        <div className="gap-3 sm:grid sm:grid-cols-2">
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Algemene opmerkingen</p>
            </header>
            <EditableTextareaComponent
              label="Vul in..."
              value={editedInspectionInfo?.generalRemarks || ""}
              isLoading={false}
              editMode={editMode}
              onChange={onUpdateGeneralRemarls}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusCodesForRooms(
                  editedInspectionInfo?.numBedrooms,
                )}`}
              />

              <p>Aantal slaapkamers</p>
            </header>
            <EditableInputComponent
              label="Vul het aantal slaapkamers in"
              value={editedInspectionInfo?.numBedrooms as any}
              editMode={editMode}
              isLoading={false}
              onChange={(value) => updateInspectionInfo({ numBedrooms: value })}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusCodesForRooms(
                  editedInspectionInfo?.numBathrooms,
                )}`}
              />

              <p>Aantal badkamers</p>
            </header>
            <EditableInputComponent
              label="Vul het aantal badkamers in"
              value={editedInspectionInfo?.numBathrooms as any}
              editMode={editMode}
              isLoading={false}
              onChange={(value) =>
                updateInspectionInfo({ numBathrooms: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusCodesForRooms(
                  editedInspectionInfo?.numToilets,
                )}`}
              />

              <p>Aantal toiletten</p>
            </header>
            <EditableInputComponent
              label="Vul het aantal toiletten in"
              value={editedInspectionInfo?.numToilets as any}
              editMode={editMode}
              isLoading={false}
              onChange={(value) => updateInspectionInfo({ numToilets: value })}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.windowStatus,
                )}`}
              />

              <p>Staat van de ramen?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(WindowStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.windowStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ windowStatus: value })
              }
            />
          </div>
          {(editedInspectionInfo?.windowStatus == WindowStatus.NOT_OK ||
            editedInspectionInfo?.windowStatus == WindowStatus.PARTIAL_OK) && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Beschrijving van eventuele gebreken aan de ramen</p>
              </header>
              <EditableTextareaComponent
                label="Geef opmerkingen of toelichting hier"
                value={editedInspectionInfo?.windowStatusDescription || ""}
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateWindowStatusDescription}
              />
            </div>
          )}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.doorStatus,
                )}`}
              />

              <p>Staat van de deuren?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(DoorStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.doorStatus}
              editMode={editMode}
              onChange={(value) => updateInspectionInfo({ doorStatus: value })}
            />
          </div>
          {(editedInspectionInfo?.doorStatus == DoorStatus.NOT_OK ||
            editedInspectionInfo?.doorStatus == DoorStatus.PARTIAL_OK) && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Beschrijving van eventuele gebreken aan de deuren</p>
              </header>
              <EditableTextareaComponent
                label="Geef opmerkingen of toelichting hier"
                value={editedInspectionInfo?.doorStatusDescription || ""}
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateDoorStatusDescription}
              />
            </div>
          )}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.moistStatus,
                )}`}
              />

              <p>Vocht of schimmel aanwezig?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(MoistStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.moistStatus}
              editMode={editMode}
              onChange={(value) => updateInspectionInfo({ moistStatus: value })}
            />
          </div>
          {editedInspectionInfo?.moistStatus == MoistStatus.NOT_OK && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>
                  Beschrijving van de locatie en situatie van de vocht of
                  schimmelproblemen
                </p>
              </header>
              <EditableTextareaComponent
                label="Geef opmerkingen of toelichting hier"
                value={editedInspectionInfo?.moistStatusDescription || ""}
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateMoistStatusDescription}
              />
            </div>
          )}
          {editedInspectionInfo?.moistStatus == MoistStatus.NOT_OK && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>Foto's vochtproblemen</p>
              </header>
              <ImagesContainer
                buildingId={building?.id as string}
                unitId={unit?.id as string}
                fileLabel={FileLabel.MoistPictureUnit}
              />
            </div>
          )}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.windowTerraceSafetyStatus,
                )}`}
              />

              <p>Veilige terrassen en lage ramen?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(WindowTerraceSafetyStatus).map(
                (value) => ({ label: value, value: value }),
              )}
              value={editedInspectionInfo?.windowTerraceSafetyStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ windowTerraceSafetyStatus: value })
              }
            />
          </div>
          {editedInspectionInfo?.windowTerraceSafetyStatus ==
            WindowTerraceSafetyStatus.NOT_OK && (
            <div className={`flex flex-col gap-3 rounded-md border p-3`}>
              <header className="flex items-center gap-2 text-sm font-medium">
                <p>
                  Beschrijving van onveilige situatie aan terrassen of lage
                  ramen
                </p>
              </header>
              <EditableTextareaComponent
                label="Geef opmerkingen of toelichting hier"
                value={
                  editedInspectionInfo?.windowTerraceSafetyStatusDescription ||
                  ""
                }
                isLoading={false}
                editMode={editMode}
                onChange={onUpdateWindowTerraceSafetyStatusDescription}
              />
            </div>
          )}
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.wallStatus,
                )}`}
              />

              <p>Staat muren?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(WallStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.wallStatus}
              editMode={editMode}
              onChange={(value) => updateInspectionInfo({ wallStatus: value })}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.ceilingStatus,
                )}`}
              />

              <p>Staat plafonds?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(CeilingStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.ceilingStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ ceilingStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColorForCeilingHeight(
                  editedInspectionInfo?.ceilingHeight,
                )}`}
              />

              <p>Wat is de afstand van vloer tot plafond? (in cm)</p>
            </header>
            <EditableInputComponent
              label="Vul de afstand in in centimeters"
              value={editedInspectionInfo?.ceilingHeight}
              editMode={editMode}
              isLoading={false}
              onChange={(value) =>
                updateInspectionInfo({ ceilingHeight: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.floorStatus,
                )}`}
              />

              <p>Staat vloeren?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(FloorStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.floorStatus}
              editMode={editMode}
              onChange={(value) => updateInspectionInfo({ floorStatus: value })}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto's afwerking muren, vloeren en plafonds</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              unitId={unit?.id as string}
              fileLabel={FileLabel.CeilingFloorWallsPicture}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.bathroomHealthStatus,
                )}`}
              />

              <p>Badkamer licht en ventilatie?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BathroomHealthStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.bathroomHealthStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ bathroomHealthStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.bedroomsHealthStatus,
                )}`}
              />

              <p>Slaapkamer licht en ventilatie?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(BedroomsHealthStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.bedroomsHealthStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ bedroomsHealthStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <span
                className={`inline-block h-5 w-5 rounded-full border border-neutral-primary/30 ${getStatusColor(
                  editedInspectionInfo?.toiletHealthStatus,
                )}`}
              />

              <p>Toilet ventilatie?</p>
            </header>
            <EditableSelectComponent
              label="Maak een keuze..."
              options={Object.values(ToiletHealthStatus).map((value) => ({
                label: value,
                value: value,
              }))}
              value={editedInspectionInfo?.toiletHealthStatus}
              editMode={editMode}
              onChange={(value) =>
                updateInspectionInfo({ toiletHealthStatus: value })
              }
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto slaapkamers</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              unitId={unit?.id as string}
              fileLabels={[PictureLabel.PictureBedrooms, PictureLabel.BedroomOne, PictureLabel.BedroomTwo, PictureLabel.BedroomThree, PictureLabel.BedroomFour]}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto badkamer</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              unitId={unit?.id as string}
              fileLabel={FileLabel.PictureBathroom}
            />
          </div>
          <div className={`flex flex-col gap-3 rounded-md border p-3`}>
            <header className="flex items-center gap-2 text-sm font-medium">
              <p>Foto toilet</p>
            </header>
            <ImagesContainer
              buildingId={building?.id as string}
              unitId={unit?.id as string}
              fileLabel={FileLabel.PictureToilet}
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default UnitVisualInspectionInfoComponent;
